import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Calendar,
    MapPin,
    CheckCircle,
    FileText,
    BookOpen,
    Award,
    ExternalLink,
    Target,
    Building2,
    Globe,
    ArrowLeft,
    Clock
} from 'lucide-react';

const BrinIseasJoinConference: React.FC = () => {
    const { i18n } = useTranslation(['public', 'common']);
    const isId = i18n.language === 'id';
    const [activeTheme, setActiveTheme] = useState<number | null>(0);
    const [activeDay, setActiveDay] = useState<'day1' | 'day2'>('day1');

    const steeringCommittee = [
        {
            name: 'Dr. Ahmad Najib Burhani, MA',
            role: 'Director General of Science and Technology',
            roleId: 'Direktur Jenderal Riset dan Teknologi',
            organization: 'Ministry of Higher Education, Science and Technology (MoHEST), Indonesia',
            organizationId: 'Kementerian Pendidikan Tinggi, Sains, dan Teknologi (Kemendiktisaintek), Indonesia',
            image: '/speakers/ahmad_najib_burhani.png',
        },
        {
            name: 'Dr. Kurniasih Mufidayati',
            role: 'Vice Chairperson of Commission X',
            roleId: 'Wakil Ketua Komisi X',
            organization: 'House of Representatives of the Republic of Indonesia',
            organizationId: 'Dewan Perwakilan Rakyat Republik Indonesia (DPR RI)',
            image: '/speakers/kurniasih_mufidayati.png',
        },
        {
            name: 'Dr. Muhammad Najib Azca',
            role: 'Chairman of Research Organization for Social Sciences and Humanities',
            roleId: 'Kepala Organisasi Riset Ilmu Pengetahuan Sosial dan Humaniora',
            organization: 'National Research and Innovation Agency (BRIN), Indonesia',
            organizationId: 'Badan Riset dan Inovasi Nasional (BRIN), Indonesia',
            image: '/speakers/muhammad_najib_azca.png',
        },
        {
            name: 'Dr. Trina Fizzanty',
            role: 'Head of Research Center for Education',
            roleId: 'Kepala Pusat Riset Pendidikan',
            organization: 'Research Organization for Social Sciences and Humanities (BRIN), Indonesia',
            organizationId: 'Organisasi Riset Ilmu Pengetahuan Sosial dan Humaniora (BRIN), Indonesia',
            image: '/speakers/trina_fizzanty.png',
        },
        {
            name: 'Ms. Julia Lau, M.A., LL.M.',
            role: 'Principal Fellow and Coordinator, Indonesia Studies Programme',
            roleId: 'Peneliti Utama dan Koordinator, Program Studi Indonesia',
            organization: 'ISEAS - Yusof Ishak Institute, Singapore',
            organizationId: 'ISEAS - Yusof Ishak Institute, Singapura',
            image: '/speakers/julia_lau.png',
        },
    ];

    const editorialTeam = [
        {
            name: 'Uswatun Hasanah, Ph.D. in Ed.',
            organization: 'Research Center for Education, Research Organization for Social Sciences and Humanities (BRIN), Indonesia',
            organizationId: 'Pusat Riset Pendidikan, Organisasi Riset Ilmu Pengetahuan Sosial dan Humaniora (BRIN), Indonesia',
            image: '/speakers/uswatun_hasanah.png',
        },
        {
            name: 'Ms. Julia Lau, M.A., LL.M.',
            organization: 'ISEAS - Yusof Ishak Institute, Singapore',
            organizationId: 'ISEAS - Yusof Ishak Institute, Singapura',
            image: '/speakers/julia_lau.png',
        },
        {
            name: 'Dr. Trina Fizzanty',
            organization: 'Research Center for Education, Research Organization for Social Sciences and Humanities (BRIN), Indonesia',
            organizationId: 'Pusat Riset Pendidikan, Organisasi Riset Ilmu Pengetahuan Sosial dan Humaniora (BRIN), Indonesia',
            image: '/speakers/trina_fizzanty.png',
        },
        {
            name: 'Prof. dr. Ardi Findyartini, Ph.D',
            organization: 'Ministry of Higher Education, Science and Technology of the Republic of Indonesia',
            organizationId: 'Kementerian Pendidikan Tinggi, Sains, dan Teknologi Republik Indonesia',
            image: '/speakers/ardi_findyartini.png',
        },
    ];

    const speakersList = [
        {
            category: 'Welcoming Remarks',
            categoryId: 'Kata Sambutan',
            name: 'Dr. Kurniasih Mufidayati',
            role: 'Vice Chairperson of Commission X of the House of Representatives of the Republic of Indonesia',
            roleId: 'Wakil Ketua Komisi X Dewan Perwakilan Rakyat Republik Indonesia (DPR RI)',
            image: '/speakers/kurniasih_mufidayati.png',
            status: '',
            statusId: ''
        },
        {
            category: 'Welcoming Remarks',
            categoryId: 'Kata Sambutan',
            name: 'Prof. Dr. Arif Satria',
            role: 'Chairman of the National Research and Innovation Agency (BRIN)',
            roleId: 'Kepala Badan Riset dan Inovasi Nasional (BRIN)',
            image: '/speakers/arif_satria.png',
            status: 'to be confirmed',
            statusId: 'masih dikonfirmasi'
        },
        {
            category: 'Opening Remark',
            categoryId: 'Pembukaan Resmi',
            name: 'Prof. Brian Yuliarto, S.T., M.Eng., Ph.D',
            role: 'Minister of Higher Education, Science, and Technology of the Republic of Indonesia',
            roleId: 'Menteri Pendidikan Tinggi, Sains, dan Teknologi Republik Indonesia',
            image: '/speakers/mendikti-saintek-prof-brian-yuliarto-ph-d.jpg',
            status: 'to be confirmed',
            statusId: 'masih dikonfirmasi'
        },
        {
            category: 'Keynote Speaker',
            categoryId: 'Pembicara Utama',
            name: 'Prof. Dr. Abdul Mu\'ti, M.Ed.',
            role: 'Minister of Primary and Secondary Education of the Republic of Indonesia',
            roleId: 'Menteri Pendidikan Dasar dan Menengah Republik Indonesia',
            image: '/speakers/mendikdasmen-prof-dr-abdul-mu_ti-m-ed.jpg',
            status: 'to be confirmed',
            statusId: 'masih dikonfirmasi'
        },
        {
            category: 'Keynote Speaker',
            categoryId: 'Pembicara Utama',
            name: 'Dr. Kurniasih Mufidayati',
            role: 'Vice Chairperson of Commission X of the House of Representatives of the Republic of Indonesia',
            roleId: 'Wakil Ketua Komisi X Dewan Perwakilan Rakyat Republik Indonesia (DPR RI)',
            image: '/speakers/kurniasih_mufidayati.png',
            status: '',
            statusId: ''
        },
        {
            category: 'Keynote Speaker',
            categoryId: 'Pembicara Utama',
            name: 'Dr. Ahmad Najib Burhani, MA',
            role: 'Director General of Science and Technology, Ministry of Higher Education, Science and Technology of the Republic of Indonesia',
            roleId: 'Direktur Jenderal Riset dan Teknologi, Kemendiktisaintek Republik Indonesia',
            image: '/speakers/ahmad_najib_burhani.png',
            status: '',
            statusId: ''
        }
    ];

    const targetedThemes = [
        {
            number: "1",
            title: "Education as Social Policy: Welfare Interventions in the School System",
            titleId: "Pendidikan sebagai Kebijakan Sosial: Intervensi Kesejahteraan dalam Sistem Sekolah",
            subtopics: [
                "The role of schools/universities in delivering social protection policies",
                "The implications of welfare-oriented programs for education financing and priorities",
                "The relationship between nutrition interventions and learning outcomes",
                "The sustainability of the nutrition program"
            ],
            subtopicsId: [
                "Peran sekolah/universitas dalam menyalurkan kebijakan perlindungan sosial",
                "Dampak program berorientasi kesejahteraan terhadap pendanaan dan prioritas pendidikan",
                "Hubungan antara intervensi gizi dan hasil belajar",
                "Keberlanjutan program gizi"
            ]
        },
        {
            number: "2",
            title: "Equity and Excellence: Reconfiguring the School System",
            titleId: "Pemerataan dan Keunggulan: Rekonfigurasi Sistem Sekolah",
            subtopics: [
                "Expanding access to education for disadvantaged communities",
                "The emergence of elite schooling models within public education systems",
                "The implications of these policies for educational inequality and social mobility"
            ],
            subtopicsId: [
                "Memperluas akses pendidikan bagi masyarakat kurang mampu",
                "Kemunculan model sekolah unggulan/elit dalam sistem pendidikan publik",
                "Dampak kebijakan ini terhadap ketimpangan pendidikan dan mobilitas sosial"
            ]
        },
        {
            number: "3",
            title: "Digitalization and the Politics of Education Technology",
            titleId: "Digitalisasi dan Politik Teknologi Pendidikan",
            subtopics: [
                "Technology-driven versus pedagogy-driven education reform",
                "Teachers' readiness and institutional capacity for digital learning",
                "The risk of technology procurement without systemic pedagogical transformation",
                "Digitalization in education: Reducing or Enlarging Social Gap"
            ],
            subtopicsId: [
                "Reformasi pendidikan berorientasi teknologi versus berorientasi pedagogi",
                "Kesiapan guru dan kapasitas institusional untuk pembelajaran digital",
                "Risiko pengadaan teknologi tanpa transformasi pedagogis yang sistemik",
                "Digitalisasi dalam pendidikan: Mengurangi atau Memperlebar Kesenjangan Sosial"
            ]
        },
        {
            number: "4",
            title: "Universities, Research, and the Developmental State",
            titleId: "Universitas, Riset, dan Negara Pembangunan (Developmental State)",
            subtopics: [
                "The relationship between universities, research institutions, and industry",
                "The alignment between research agendas and national strategic priorities",
                "Academic autonomy and the governance of knowledge production",
                "International collaboration in establishing ten new medical and STEM universities"
            ],
            subtopicsId: [
                "Hubungan antara universitas, lembaga riset, dan industri",
                "Kelarasan antara agenda riset dan prioritas strategis nasional",
                "Otonomi akademik dan tata kelola produksi pengetahuan",
                "Kolaborasi internasional dalam mendirikan sepuluh universitas baru di bidang medis dan STEM"
            ]
        },
        {
            number: "5",
            title: "Comparative Higher Education: Lessons Learned for Indonesia",
            titleId: "Studi Komparatif Pendidikan Tinggi: Pembelajaran untuk Indonesia",
            subtopics: [
                "Transforming Higher Education System",
                "Best talent circulation program",
                "STEAM education and Industrialization",
                "Vocational Education"
            ],
            subtopicsId: [
                "Transformasi Sistem Pendidikan Tinggi",
                "Program sirkulasi talenta terbaik",
                "Pendidikan STEAM dan Industrialisasi",
                "Pendidikan Vokasi"
            ]
        },
        {
            number: "6",
            title: "Power Dynamics of Educational Governance",
            titleId: "Dinamika Kekuasaan Tata Kelola Pendidikan",
            subtopics: [
                "Who ultimately governs education?",
                "How are policies coordinated across institutions?",
                "How does fragmentation in educational governance occur?",
                "How can education policy coordination be improved?",
                "What governance structures are needed for effective reform?",
                "How do the structural shifts take place, from local-government initiatives to the highly centralised national education program?",
                "How do power, policy and ideology intersect to shape human capital?"
            ],
            subtopicsId: [
                "Siapa yang pada akhirnya mengelola pendidikan?",
                "Bagaimana koordinasi kebijakan antar institusi dilakukan?",
                "Bagaimana fragmentasi dalam tata kelola pendidikan bisa terjadi?",
                "Bagaimana koordinasi kebijakan pendidikan dapat ditingkatkan?",
                "Struktur tata kelola seperti apa yang dibutuhkan untuk reformasi yang efektif?",
                "Bagaimana pergeseran struktural terjadi, dari inisiatif pemerintah daerah ke program pendidikan nasional yang terpusat?",
                "Bagaimana kekuasaan, kebijakan, dan ideologi saling bersinggungan dalam membentuk modal manusia?"
            ]
        }
    ];

    const importantDates = [
        {
            date: "July 1st - 31st, 2026",
            dateId: "1 - 31 Juli 2026",
            title: "Submission of Extended Abstract",
            titleId: "Pengumpulan Abstrak Diperluas",
            desc: "Minimum 500 words with draft title indicating theme, plus short author biography (maximum 100 words).",
            descId: "Minimal 500 kata dengan draf judul yang mencantumkan tema, ditambah biografi singkat penulis (maksimal 100 kata).",
            highlight: true
        },
        {
            date: "August 21st, 2026",
            dateId: "21 Agustus 2026",
            title: "Announcement of Acceptance",
            titleId: "Pengumuman Penerimaan Abstrak",
            desc: "Notification will be sent directly to the corresponding author's email.",
            descId: "Pemberitahuan akan dikirimkan langsung ke email penulis korespondensi.",
            highlight: false
        },
        {
            date: "Sept 21st, 2026",
            dateId: "21 September 2026",
            title: "Final Deadline for 10-12 Page Conference Draft",
            titleId: "Batas Waktu Akhir untuk Draf Makalah 10-12 Halaman",
            desc: "The conference draft should consist of introduction, literature review, methodology, and initial findings.",
            descId: "Draf makalah konferensi harus terdiri dari pendahuluan, tinjauan pustaka, metodologi, dan temuan awal.",
            highlight: true
        },
        {
            date: "Sept 24th, 2026",
            dateId: "24 September 2026",
            title: "Submission of Presentation Slides (Optional)",
            titleId: "Pengumpulan Slide Presentasi (Opsional)",
            desc: "Slides become optional in this conference; participant can present directly using the submitted draft.",
            descId: "Slide bersifat opsional pada konferensi ini; peserta dapat mempresentasikan langsung menggunakan draf yang dikirimkan.",
            highlight: false
        },
        {
            date: "October 7th - 8th, 2026",
            dateId: "7 - 8 Oktober 2026",
            title: "Joint Conference (Post-Convention Hybrid Summit)",
            titleId: "Konferensi Bersama (Summit Hybrid Pasca-Konvensi)",
            desc: "Held in Jakarta, Indonesia.",
            descId: "Diselenggarakan di Jakarta, Indonesia.",
            highlight: true
        }
    ];

    const rundownDay1 = [
        { time: "08.00 am - 09.00 am", activity: "Registration", activityId: "Registrasi" },
        { time: "09.00 am – 09.20 am", activity: "Welcoming and Opening Remarks", activityId: "Sambutan dan Pembukaan Resmi" },
        { time: "09.20 am – 09.40 am", activity: "Keynote Speaker-1", activityId: "Pembicara Utama 1" },
        { time: "09.40 am – 10.00 am", activity: "Keynote Speaker-2", activityId: "Pembicara Utama 2" },
        { time: "10.00 am – 10.30 am", activity: "Tea break", activityId: "Rehat Teh / Kopi" },
        { time: "10.30 am – 12.00 pm", activity: "Panel (1st-a)", activityId: "Sesi Panel (1-a)" },
        { time: "12.00 pm – 13.30 pm", activity: "Lunch break", activityId: "Istirahat Makan Siang" },
        { time: "13.30 pm - 15.00 pm", activity: "Panel (1st-b)", activityId: "Sesi Panel (1-b)" },
        { time: "15.00 pm – 15.30 pm", activity: "Tea break", activityId: "Rehat Teh / Kopi" },
        { time: "15.30 pm- 17.00 pm", activity: "Panel (2nd)", activityId: "Sesi Panel (2)" },
        { time: "18.00 pm – 20.00 pm", activity: "Welcome Dinner", activityId: "Makan Malam Penyambutan" },
    ];

    const rundownDay2 = [
        { time: "08.00 am - 09.00 am", activity: "Registration", activityId: "Registrasi" },
        { time: "09.00 am – 09.30 am", activity: "Keynote speaker 3", activityId: "Pembicara Utama 3" },
        { time: "09.30 am – 11.00 am", activity: "Panel (3rd)", activityId: "Sesi Panel (3)" },
        { time: "11.00 am – 11.15 am", activity: "Tea break", activityId: "Rehat Teh / Kopi" },
        { time: "11.15 am – 12.45 pm", activity: "Panel (4th)", activityId: "Sesi Panel (4)" },
        { time: "12.45 pm – 13.45 pm", activity: "Lunch break", activityId: "Istirahat Makan Siang" },
        { time: "13.45 pm – 15.15 pm", activity: "Panel (5th)", activityId: "Sesi Panel (5)" },
        { time: "15.15 pm – 15.30 pm", activity: "Tea break", activityId: "Rehat Teh / Kopi" },
        { time: "15.30 pm – 17.00 pm", activity: "Panel (6th)", activityId: "Sesi Panel (6)" },
        { time: "17.00 pm – 17.30 pm", activity: "Wrap Up and Closing Session", activityId: "Rangkuman dan Sesi Penutupan" },
    ];

    const aims = [
        {
            en: "Critically examine recent policy initiatives in Indonesia’s education and research sectors, including SMA Unggul Garuda Program.",
            id: "Mengkaji secara kritis inisiatif kebijakan terbaru di sektor pendidikan dan riset Indonesia, termasuk Program SMA Unggul Garuda."
        },
        {
            en: "Analyze how current policies reshape the relationship between education, social policy, and national development strategies.",
            id: "Menganalisis bagaimana kebijakan saat ini membentuk kembali hubungan antara pendidikan, kebijakan sosial, dan strategi pembangunan nasional."
        },
        {
            en: "Explore the evolving role of universities and research institutions in Indonesia’s innovation and knowledge ecosystem.",
            id: "Mengeksplorasi peran universitas dan lembaga riset yang terus berkembang dalam ekosistem inovasi dan pengetahuan Indonesia."
        },
        {
            en: "Generate scholarly contributions that advance critical debates on education and research governance in Indonesia.",
            id: "Menghasilkan kontribusi ilmiah yang memajukan perdebatan kritis tentang tata kelola pendidikan dan riset di Indonesia."
        }
    ];

    return (
        <section className="dark:from-gray-900 dark:to-gray-800 py-5 md:py-10 relative overflow-hidden border-y border-border">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-amber-50/30 to-blue-100/50 dark:from-blue-950/20 dark:via-amber-950/10 dark:to-blue-900/30"></div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-7xl mx-auto">
                    {/* Top Navigation Bar */}
                    <div className="flex flex-wrap items-center justify-between gap-4 pt-4 mb-10 pb-5 border-b border-border/40">
                        <Link
                            to="/#conference-tracks"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white dark:bg-gray-800 hover:bg-primary hover:text-primary-foreground text-foreground dark:text-white font-semibold text-sm shadow-md border border-border/80 transition-all duration-300 transform hover:-translate-x-1 group"
                        >
                            <ArrowLeft className="w-4 h-4 text-primary group-hover:text-primary-foreground transition-colors" />
                            {isId ? 'Kembali ke Daftar Track Konferensi' : 'Back to Conference Tracks'}
                        </Link>
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-blue-500/10 dark:from-emerald-500/20 dark:to-blue-500/20 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 px-4 py-2 rounded-full text-xs sm:text-sm font-bold tracking-wider uppercase shadow-sm">
                            <span>KONASPI XII 2026 • FEATURED JOINT TRACK</span>
                        </div>
                    </div>

                    {/* Two-Part Event Series Info Banner */}
                    {/* <div className="mb-10 flex flex-col sm:flex-row items-stretch gap-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 p-5">
                        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-bold text-xs uppercase tracking-wider shrink-0 sm:pr-4 sm:border-r sm:border-amber-500/20">
                            <Clock className="w-4 h-4" />
                            {isId ? 'Rangkaian Dua Sesi' : 'Two-Part Session Series'}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 text-sm">
                            <div className="flex items-start gap-2">
                                <span className="mt-0.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-extrabold px-2.5 py-0.5 text-[11px] uppercase tracking-wide">
                                    {isId ? 'Sedang Berlangsung' : 'Happening Now'}
                                </span>
                                <span className="text-muted-foreground">
                                    <strong className="text-foreground dark:text-white">{isId ? 'Sesi Pra-Konvensi' : 'Pre-Convention Session'}</strong> — {isId ? '27-28 Agustus 2026' : 'August 27-28, 2026'}
                                </span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="mt-0.5 inline-flex items-center gap-1.5 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-400 font-extrabold px-2.5 py-0.5 text-[11px] uppercase tracking-wide">
                                    {isId ? 'Akan Datang' : 'Upcoming'}
                                </span>
                                <span className="text-muted-foreground">
                                    <strong className="text-foreground dark:text-white">{isId ? 'Sesi Pasca-Konvensi (Konferensi Bersama)' : 'Post-Convention Session (Joint Conference)'}</strong> — {isId ? '7-8 Oktober 2026, Jakarta' : 'October 7-8, 2026, Jakarta'}
                                </span>
                            </div>
                        </div>
                    </div> */}

                    {/* Coursespace-Style Asymmetrical 2-Column Grid Hero Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-20">
                        {/* Left Column: Typography & Action Buttons */}
                        <div className="lg:col-span-7 text-left">
                            <div className="inline-block bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 font-extrabold px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-6">
                                KEMENDIKTISAINTEK • BRIN • ISEAS • UNDIKSHA
                            </div>

                            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-[1.15] mb-6">
                                {isId ? 'Pendidikan di' : 'Education in'} <br className="hidden sm:inline" />
                                <span className="relative inline-block text-emerald-600 dark:text-emerald-400">
                                    {isId ? 'Indonesia Kontemporer' : 'Contemporary Indonesia'}
                                    {/* Curved Wavy Brush Underline exactly like Coursespace */}
                                    <svg className="absolute -bottom-2 sm:-bottom-3 left-0 w-full h-3 sm:h-4 text-amber-500" viewBox="0 0 200 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3 11C50 3 150 3 197 11" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                                    </svg>
                                </span>
                            </h1>

                            <p className="text-lg sm:text-xl md:text-2xl font-medium text-slate-600 dark:text-slate-300 leading-relaxed mb-8 text-pretty italic border-l-4 border-emerald-500 pl-4 py-1">
                                {isId ? '“Navigasi Transisi Tata Kelola dan Dampak Modal Manusia”' : '“Navigating Governance Transitions and Human Capital Impact”'}
                            </p>

                            <div className="flex flex-wrap items-center gap-4">
                                <a
                                    href="https://s.brin.go.id/m/joint_hybrid_conference"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-8 sm:px-10 rounded-full shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 transform hover:-translate-y-0.5 text-base sm:text-lg group"
                                >
                                    <FileText className="w-5 h-5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span>{isId ? 'Kirim via Portal BRIN' : 'Submit via BRIN Portal'}</span>
                                    <ExternalLink className="w-4 h-4 ml-1 flex-shrink-0 opacity-80" />
                                </a>
                                <a
                                    href="#about-conference"
                                    className="inline-flex items-center justify-center gap-2.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-slate-800 dark:text-white border border-gray-300 dark:border-gray-600 font-bold py-4 px-8 rounded-full shadow-sm hover:shadow transition-all duration-300 text-base sm:text-lg"
                                >
                                    <BookOpen className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                    <span>{isId ? 'Pelajari Selengkapnya' : 'Learn More'}</span>
                                </a>
                            </div>
                        </div>

                        {/* Right Column: Visual Centerpiece with Floating Cards (Like Coursespace image) */}
                        <div className="lg:col-span-5 relative flex justify-center py-6">
                            {/* Decorative Background Glow blob */}
                            <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/20 via-amber-500/15 to-blue-500/20 rounded-full blur-3xl -z-10 transform scale-90 pointer-events-none"></div>

                            {/* Main Framed Glass Centerpiece */}
                            <div className="bg-white dark:bg-gray-800/95 p-8 sm:p-10 md:p-12 rounded-[2.5rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.12)] dark:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] border border-border/80 relative z-10 w-full max-w-md text-center flex flex-col items-center justify-center transform hover:scale-[1.01] transition-all duration-500">
                                <img
                                    src="/logo/kemendiktisaintek-brin-iseas-konaspi-xii-undiksha.png"
                                    alt="KEMENDIKTISAINTEK - BRIN - ISEAS - KONASPI XII UNDIKSHA Joint Conference"
                                    className="max-h-28 sm:max-h-36 w-auto object-contain mx-auto mb-6"
                                />
                                <div className="pt-5 border-t border-border/50 w-full">
                                    <span className="inline-block bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 font-bold px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border border-emerald-500/20">
                                        {isId ? 'KTT Hybrid Bersama Resmi' : 'Official Joint Hybrid Summit'}
                                    </span>
                                </div>
                            </div>

                            {/* Floating Decorative Card 1: Free Fee (Top Right) */}
                            <div className="absolute -top-4 -right-2 sm:-right-6 bg-white dark:bg-gray-800 p-3.5 sm:p-4 rounded-2xl shadow-xl border border-emerald-500/30 flex items-center gap-3 z-20 animate-bounce-slow">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                                    <CheckCircle className="w-5 h-5" />
                                </div>
                                <div className="text-left">
                                    <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">{isId ? 'Pendaftaran' : 'Registration'}</div>
                                    <div className="text-xs sm:text-sm font-extrabold text-emerald-600 dark:text-emerald-400">{isId ? '100% Gratis (Rp 0)' : '100% Free (Rp 0)'}</div>
                                </div>
                            </div>

                            {/* Floating Decorative Card 2: Date & Venue (Bottom Left) */}
                            <div className="absolute -bottom-4 -left-2 sm:-left-6 bg-white dark:bg-gray-800 p-3.5 sm:p-4 rounded-2xl shadow-xl border border-blue-500/30 flex items-center gap-3 z-20">
                                <div className="w-10 h-10 rounded-xl bg-blue-500/15 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                                    <Calendar className="w-5 h-5" />
                                </div>
                                <div className="text-left">
                                    <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">{isId ? 'Jadwal Konferensi' : 'Conference Schedule'}</div>
                                    <div className="text-xs sm:text-sm font-extrabold text-slate-800 dark:text-white">{isId ? '7 - 8 Okt 2026' : 'Oct 7 - 8th, 2026'}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Coursespace-Style Horizontal 3-Stat White Glass Ribbon */}
                    <div className="bg-white dark:bg-gray-800/95 rounded-3xl p-8 sm:p-10 shadow-xl border border-border/70 grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-border/60 mb-16">
                        <div className="pt-4 md:pt-0 flex flex-col items-center justify-center">
                            <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-emerald-600 dark:text-emerald-400 mb-1">
                                Rp 0
                            </span>
                            <span className="text-sm font-extrabold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                                {isId ? 'Gratis Biaya Konferensi' : 'Free Conference Fee'}
                            </span>
                            <span className="text-xs text-muted-foreground mt-1">
                                {isId ? 'Tanpa biaya untuk peserta & pemakalah terpilih' : 'No fee for accepted participants & presenters'}
                            </span>
                        </div>

                        <div className="pt-6 md:pt-0 flex flex-col items-center justify-center">
                            <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-amber-500 mb-1">
                                {isId ? '7-8 Okt' : 'Oct 7-8'}
                            </span>
                            <span className="text-sm font-extrabold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                                {isId ? 'Tanggal KTT 2026' : '2026 Summit Dates'}
                            </span>
                            <span className="text-xs text-muted-foreground mt-1">
                                {isId ? '2 Hari sesi panel bersama yang berdampak tinggi' : '2 Days of high-impact joint panel sessions'}
                            </span>
                        </div>

                        <div className="pt-6 md:pt-0 flex flex-col items-center justify-center">
                            <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-blue-600 dark:text-blue-400 mb-1">
                                Hybrid
                            </span>
                            <span className="text-sm font-extrabold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                                {isId ? 'Jakarta & Daring' : 'Jakarta & Online'}
                            </span>
                            <span className="text-xs text-muted-foreground mt-1">
                                {isId ? 'Jakarta, Indonesia & Virtual' : 'Jakarta, Indonesia & Virtual'}
                            </span>
                        </div>
                    </div>

                    {/* Organized By Logos Section */}
                    <div className="mb-16 bg-white/70 dark:bg-gray-900/70 rounded-2xl p-6 md:p-8 border border-border shadow-md">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground text-center mb-6">
                            {isId ? 'Diselenggarakan Bersama Oleh' : 'Jointly Organized By'}
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center justify-items-center text-center">
                            <div className="flex flex-col items-center gap-2 p-3">
                                <div className="w-14 h-14 bg-blue-50 dark:bg-blue-950/50 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 shadow-inner">
                                    <Building2 className="w-7 h-7" />
                                </div>
                                <span className="text-xs md:text-sm font-bold text-foreground">MoHEST</span>
                                <span className="text-[11px] text-muted-foreground">{isId ? 'Direktorat Jenderal Riset & Teknologi, Indonesia' : 'Directorate General of Science & Technology, Indonesia'}</span>
                            </div>
                            <div className="flex flex-col items-center gap-2 p-3">
                                <div className="w-14 h-14 bg-red-50 dark:bg-red-950/50 rounded-full flex items-center justify-center text-red-600 dark:text-red-400 shadow-inner">
                                    <Globe className="w-7 h-7" />
                                </div>
                                <span className="text-xs md:text-sm font-bold text-foreground">BRIN</span>
                                <span className="text-[11px] text-muted-foreground">{isId ? 'Organisasi Riset Ilmu Pengetahuan Sosial & Humaniora' : 'Research Organization for Social Sciences & Humanities'}</span>
                            </div>
                            <div className="flex flex-col items-center gap-2 p-3">
                                <div className="w-14 h-14 bg-amber-50 dark:bg-amber-950/50 rounded-full flex items-center justify-center text-amber-600 dark:text-amber-400 shadow-inner">
                                    <Award className="w-7 h-7" />
                                </div>
                                <span className="text-xs md:text-sm font-bold text-foreground">ISEAS</span>
                                <span className="text-[11px] text-muted-foreground">{isId ? 'Yusof Ishak Institute, Singapura' : 'Yusof Ishak Institute, Singapore'}</span>
                            </div>
                            <div className="flex flex-col items-center gap-2 p-3">
                                <div className="w-14 h-14 bg-green-50 dark:bg-green-950/50 rounded-full flex items-center justify-center text-green-600 dark:text-green-400 shadow-inner">
                                    <BookOpen className="w-7 h-7" />
                                </div>
                                <span className="text-xs md:text-sm font-bold text-foreground">Undiksha</span>
                                <span className="text-[11px] text-muted-foreground">{isId ? 'KONASPI XII, Universitas Pendidikan Ganesha' : 'KONASPI XII, Ganesha University of Education'}</span>
                            </div>
                        </div>
                    </div>

                    {/* About & Objectives Grid */}
                    <div className="grid lg:grid-cols-12 gap-8 mb-20">
                        <div className="lg:col-span-7 bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-xl border border-border space-y-6">
                            <h3 className="text-2xl font-bold text-primary dark:text-blue-400 flex items-center gap-3">
                                <BookOpen className="w-6 h-6" />
                                {isId ? 'Tentang Konferensi Bersama' : 'About the Joint Conference'}
                            </h3>
                            <div className="space-y-4 text-muted-foreground dark:text-gray-300 leading-relaxed text-sm md:text-base">
                                <p>
                                    {isId
                                        ? 'Diselenggarakan bersama oleh Direktorat Jenderal Riset dan Teknologi (MoHEST) Indonesia, Organisasi Riset Ilmu Pengetahuan Sosial dan Humaniora – Badan Riset dan Inovasi Nasional (BRIN), ISEAS – Yusof Ishak Institute (Singapura), dan KONASPI XII Universitas Pendidikan Ganesha (Undiksha), konferensi ini mempertemukan para pakar terkemuka dari dalam dan luar negeri untuk mendorong diskusi berbasis bukti serta kolaborasi internasional mengenai kebijakan dan riset pendidikan kontemporer.'
                                        : 'Jointly organized by the Directorate General of Science and Technology (MoHEST), Indonesia, Research Organization for Social Sciences and Humanities – National Research and Innovation Agency (BRIN), ISEAS – Yusof Ishak Institute (Singapore), and KONASPI XII, Ganesha University of Education (Undiksha), the conference brings together leading experts from Indonesia and abroad to foster evidence-based discussion and international collaboration on contemporary education policy and research.'}
                                </p>
                                <p>
                                    {isId
                                        ? <>Sebagai bagian dari transformasi pendidikan Indonesia yang terus berlangsung, konferensi ini juga mendukung pelaksanaan beberapa program unggulan yang menandakan arah kebijakan baru, seperti pendirian <strong className="text-foreground dark:text-white">SMA Unggul Garuda</strong>. Program SMA Unggul Garuda merupakan salah satu inisiatif strategis Pemerintah Indonesia untuk mengembangkan sekolah menengah atas unggulan berbiaya pemerintah sebagai pilar utama kerangka <strong className="text-foreground dark:text-white">Manajemen Talenta Nasional</strong>.</>
                                        : <>As part of Indonesia's ongoing education transformation, this conference also supports the implementation of several flagship programs that signal new policy directions such as the establishment of <strong className="text-foreground dark:text-white">SMA Unggul Garuda</strong>. SMA Unggul Garuda Program is one of the Government of Indonesia's strategic initiatives to develop government-funded excellent senior high schools as a cornerstone of the <strong className="text-foreground dark:text-white">National Talent Management (Manajemen Talenta Nasional)</strong> framework.</>}
                                </p>
                                <p>
                                    {isId
                                        ? 'Oleh karena itu, konferensi bersama ini menyediakan wadah internasional bagi para akademisi, peneliti, pembuat kebijakan, pendidik, dan praktisi untuk mengkaji secara kritis perkembangan tersebut beserta dampak luasnya. Melalui pidato utama, diskusi panel, presentasi ilmiah, dan dialog akademis, para peserta akan mengeksplorasi bagaimana pendidikan dan riset dapat berkontribusi pada pembangunan nasional berkelanjutan sembari menjawab tantangan transisi tata kelola dan reformasi kelembagaan.'
                                        : 'This joint conference therefore provides an international platform for scholars, researchers, policymakers, educators, and practitioners to critically examine these developments and their broader implications. Through keynote speeches, panel discussions, academic presentations, and scholarly dialogue, participants will explore how education and research can contribute to sustainable national development while addressing the challenges of governance transitions and institutional reform.'}
                                </p>
                                <div className="p-4 bg-gradient-to-r from-amber-500/10 to-blue-500/10 rounded-xl border border-amber-500/20 text-foreground dark:text-white font-medium text-sm">
                                    🌟 <strong className="text-primary dark:text-amber-400">{isId ? 'Lokakarya & Publikasi Ilmiah:' : 'Academic Workshop & Publication:'}</strong> {isId ? <>Selain menjadi wadah pertukaran ilmiah, makalah yang diterima akan dikembangkan menjadi bab-bab dalam <strong className="underline">buku publikasi internasional bersanad (peer-reviewed edited volume) oleh penerbit akademis internasional</strong>. Dengan menghubungkan riset, kebijakan, dan praktik, konferensi ini berupaya menghasilkan kontribusi bermakna bagi masa depan tata kelola pendidikan dan riset.</> : <>Beyond serving as a forum for scholarly exchange, accepted papers will be developed into chapters for a <strong className="underline">peer-reviewed edited volume to be published by an international academic publisher</strong>. By connecting research, policy, and practice, the conference seeks to generate meaningful contributions to support the future of education and research governance.</>}
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
                            <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-xl border border-border flex-1">
                                <h3 className="text-xl font-bold text-foreground dark:text-white flex items-center gap-3 mb-6">
                                    <Target className="w-6 h-6 text-amber-600" />
                                    {isId ? 'Tujuan & Sasaran Konferensi' : 'Conference Aims & Objectives'}
                                </h3>
                                <ul className="space-y-4">
                                    {aims.map((aim, idx) => (
                                        <li key={idx} className="flex items-start gap-3 text-sm text-muted-foreground dark:text-gray-300">
                                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                                                {idx + 1}
                                            </span>
                                            <span className="leading-relaxed">{isId ? aim.id : aim.en}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Facilities Banner */}
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-3xl p-6 shadow-xl space-y-3">
                                <h4 className="font-bold text-base flex items-center gap-2">
                                    <Award className="w-5 h-5 text-amber-300" />
                                    {isId ? 'Fasilitas Pemakalah & Peserta' : 'Presenter & Participant Facilities'}
                                </h4>
                                <div className="grid grid-cols-2 gap-2 text-xs md:text-sm opacity-95">
                                    <div className="flex items-center gap-2">✓ {isId ? 'E-Sertifikat' : 'E-Certificate'}</div>
                                    <div className="flex items-center gap-2">✓ {isId ? 'Kit Seminar' : 'Seminar Kit'}</div>
                                    <div className="flex items-center gap-2">✓ {isId ? 'Buku Publikasi (Makalah Terpilih)' : 'Edited Volume (Selected Papers)'}</div>
                                    <div className="flex items-center gap-2">✓ {isId ? 'Sesi Jejaring / Networking' : 'Networking Session'}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Steering Committee & Editorial Team Sections */}
                    <div className="mb-20 space-y-16">
                        {/* Steering Committee */}
                        <div>
                            <div className="text-center mb-10">
                                <span className="text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-3 inline-block">
                                    {isId ? 'Kepemimpinan & Tata Kelola' : 'Leadership & Governance'}
                                </span>
                                <h3 className="text-3xl font-bold text-foreground dark:text-white mb-3">
                                    {isId ? 'Komite Pengarah (Steering Committee)' : 'Steering Committee'}
                                </h3>
                                <p className="text-muted-foreground max-w-2xl mx-auto">
                                    {isId ? 'Mengarahkan kolaborasi strategis dan visi akademis antara MoHEST, BRIN, ISEAS, dan Undiksha.' : 'Guiding the strategic collaboration and academic vision between MoHEST, BRIN, ISEAS, and Undiksha.'}
                                </p>
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
                                {steeringCommittee.map((member, idx) => (
                                    <div key={idx} className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-border shadow-md hover:shadow-xl transition-all flex flex-col items-center text-center">
                                        <div className="w-32 sm:w-36 aspect-[4/5] rounded-xl bg-gradient-to-br from-blue-500 via-emerald-500 to-amber-500 p-0.5 mb-4 shadow-md overflow-hidden relative group/img flex-shrink-0">
                                            <div className="w-full h-full bg-slate-100 dark:bg-slate-800 rounded-[10px] flex items-center justify-center text-2xl font-extrabold text-primary tracking-wider">
                                                {member.name.replace(/Dr\.|Prof\.|dr\.|S\.T\.|M\.Eng\.|Ph\.D|MA|M\.Ed\.|M\.A\.|LL\.M\./g, '').trim().split(/\s+/).map(n => n[0]).slice(0, 2).join('')}
                                            </div>
                                            {member.image && (
                                                <img
                                                    src={member.image}
                                                    alt={member.name}
                                                    className="absolute inset-0 w-full h-full object-cover object-top rounded-[10px] transition-transform duration-500 group-hover/img:scale-105"
                                                    onError={(e) => { (e.currentTarget as HTMLElement).style.display = 'none'; }}
                                                />
                                            )}
                                        </div>
                                        <h4 className="font-bold text-sm sm:text-base text-foreground dark:text-white mb-1 leading-snug">
                                            {member.name}
                                        </h4>
                                        <p className="text-xs font-semibold text-primary dark:text-blue-400 mb-2">
                                            {isId ? (member.roleId || member.role) : member.role}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground mt-auto">
                                            {isId ? (member.organizationId || member.organization) : member.organization}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Editorial Team */}
                        <div>
                            <div className="text-center mb-10">
                                <h3 className="text-2xl sm:text-3xl font-bold text-foreground dark:text-white mb-3">
                                    {isId ? 'Tim Editorial' : 'The Editorial Team'}
                                </h3>
                                <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base">
                                    {isId ? 'Mengelola penelaahan ilmiah dan persiapan makalah yang diterima menjadi bab buku untuk publikasi peer-reviewed.' : 'Managing scholarly review and preparation of accepted papers into chapters for peer-reviewed edited volume.'}
                                </p>
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {editorialTeam.map((member, idx) => (
                                    <div key={idx} className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-border shadow-md hover:shadow-xl transition-all flex flex-col items-center text-center">
                                        <div className="w-32 sm:w-36 aspect-[4/5] rounded-xl bg-gradient-to-br from-emerald-500 via-teal-500 to-blue-500 p-0.5 mb-4 shadow-md overflow-hidden relative group/img flex-shrink-0">
                                            <div className="w-full h-full bg-slate-100 dark:bg-slate-800 rounded-[10px] flex items-center justify-center text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 tracking-wider">
                                                {member.name.replace(/Dr\.|Prof\.|dr\.|S\.T\.|M\.Eng\.|Ph\.D|MA|M\.Ed\.|M\.A\.|LL\.M\.|in Ed\./g, '').trim().split(/\s+/).map(n => n[0]).slice(0, 2).join('')}
                                            </div>
                                            {member.image && (
                                                <img
                                                    src={member.image}
                                                    alt={member.name}
                                                    className="absolute inset-0 w-full h-full object-cover object-top rounded-[10px] transition-transform duration-500 group-hover/img:scale-105"
                                                    onError={(e) => { (e.currentTarget as HTMLElement).style.display = 'none'; }}
                                                />
                                            )}
                                        </div>
                                        <h4 className="font-bold text-base text-foreground dark:text-white mb-1 leading-snug">
                                            {member.name}
                                        </h4>
                                        <p className="text-xs text-muted-foreground mt-auto">
                                            {isId ? (member.organizationId || member.organization) : member.organization}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Speakers Section (Page 3 & 4 of PDF) */}
                    <div className="mb-20">
                        <div className="text-center mb-12">
                            <span className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 rounded-full mb-3 inline-block">
                                {isId ? 'Pembicara Pilihan' : 'Featured Speakers'}
                            </span>
                            <h3 className="text-3xl md:text-4xl font-bold text-foreground dark:text-white mb-4">
                                {isId ? 'Sambutan Pembukaan & Pembicara Utama' : 'Welcoming, Opening Remarks & Keynote Speakers'}
                            </h3>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                {isId ? 'Para pemimpin pemerintahan, pembuat kebijakan, dan akademisi terkemuka yang mengkaji transisi pendidikan kontemporer.' : 'Distinguished government leaders, policymakers, and academics addressing contemporary education transitions.'}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {speakersList.map((speaker, idx) => (
                                <div key={idx} className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-border shadow-md hover:shadow-xl transition-all flex flex-col justify-between relative overflow-hidden group">
                                    <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-amber-500 via-emerald-500 to-blue-500"></div>
                                    <div>
                                        <div className="flex items-center justify-between gap-2 mb-4">
                                            <span className="text-xs font-bold uppercase px-3 py-1 rounded-full bg-primary/10 text-primary">
                                                {isId ? (speaker.categoryId || speaker.category) : speaker.category}
                                            </span>
                                            {speaker.status && (
                                                <span className="text-[11px] font-extrabold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-500/30">
                                                    ({speaker.status})
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5 mb-4">
                                            <div className="w-28 sm:w-32 aspect-[4/5] rounded-xl bg-gradient-to-br from-amber-500 via-emerald-500 to-blue-500 p-0.5 flex-shrink-0 relative overflow-hidden group/img shadow-md">
                                                <div className="w-full h-full bg-slate-100 dark:bg-slate-800 rounded-[10px] flex items-center justify-center text-2xl font-extrabold text-primary tracking-wider">
                                                    {speaker.name.replace(/Dr\.|Prof\.|dr\.|S\.T\.|M\.Eng\.|Ph\.D|MA|M\.Ed\.|M\.A\.|LL\.M\./g, '').trim().split(/\s+/).map(n => n[0]).slice(0, 2).join('')}
                                                </div>
                                                {speaker.image && (
                                                    <img
                                                        src={speaker.image}
                                                        alt={speaker.name}
                                                        className="absolute inset-0 w-full h-full object-cover object-top rounded-[10px] transition-transform duration-500 group-hover/img:scale-105"
                                                        onError={(e) => { (e.currentTarget as HTMLElement).style.display = 'none'; }}
                                                    />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0 text-center sm:text-left py-1">
                                                <h4 className="font-bold text-base sm:text-lg text-foreground dark:text-white leading-snug mb-2">
                                                    {speaker.name}
                                                </h4>
                                                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                                                    {isId ? (speaker.roleId || speaker.role) : speaker.role}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Targeted Themes Section */}
                    <div className="mb-20">
                        <div className="text-center mb-12">
                            <span className="text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-3 inline-block">
                                {isId ? 'Undangan Makalah (Call For Papers)' : 'Call For Papers'}
                            </span>
                            <h3 className="text-3xl md:text-4xl font-bold text-foreground dark:text-white mb-4">
                                {isId ? 'Tema Sasaran (6 Tema)' : 'Targeted Themes (6 Themes)'}
                            </h3>
                            <p className="text-muted-foreground max-w-3xl mx-auto">
                                {isId ? 'Kami mengundang akademisi, peneliti, pembuat kebijakan, dan pendidik untuk mengirimkan abstrak diperluas (extended abstract) sesuai dengan salah satu tema kunci berikut:' : 'We invite interested scholars, researchers, policymakers, and educators to submit extended abstracts under one of the following key themes:'}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {targetedThemes.map((theme, index) => {
                                const subList = (isId && theme.subtopicsId) ? theme.subtopicsId : theme.subtopics;
                                return (
                                    <div
                                        key={index}
                                        className={`bg-white dark:bg-gray-900 rounded-2xl border transition-all duration-300 p-6 flex flex-col justify-between cursor-pointer ${activeTheme === index
                                            ? 'border-primary shadow-xl ring-2 ring-primary/20'
                                            : 'border-border shadow-md hover:border-primary/50'
                                            }`}
                                        onClick={() => setActiveTheme(index)}
                                    >
                                        <div>
                                            <div className="flex items-center justify-between mb-4">
                                                <span className="w-8 h-8 rounded-lg bg-primary text-primary-foreground font-bold flex items-center justify-center text-sm shadow-sm">
                                                    {theme.number}
                                                </span>
                                                <span className="text-xs font-semibold text-muted-foreground">{isId ? `Tema ${theme.number} dari 6` : `Theme ${theme.number} of 6`}</span>
                                            </div>
                                            <h4 className="font-bold text-lg text-foreground dark:text-white mb-3 leading-snug">
                                                {isId ? (theme.titleId || theme.title) : theme.title}
                                            </h4>
                                            <div className="space-y-2 mt-4">
                                                {subList.map((sub, sIdx) => (
                                                    <div key={sIdx} className="flex items-start gap-2 text-xs text-muted-foreground dark:text-gray-300">
                                                        <span className="text-primary mt-0.5">•</span>
                                                        <span>{sub}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Conference Schedule / Rundown (Page 8 of PDF) */}
                    <div className="mb-20 bg-white dark:bg-gray-900 rounded-3xl p-8 sm:p-10 border border-border shadow-xl">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 border-b border-border/60 pb-6">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 bg-blue-500/10 px-3 py-1.5 rounded-full mb-2 inline-block">
                                    {isId ? 'Halaman 7 & 8 • Jadwal Konferensi' : 'Page 7 & 8 • Conference Schedule'}
                                </span>
                                <h3 className="text-2xl sm:text-3xl font-bold text-foreground dark:text-white flex items-center gap-3 mt-1">
                                    <Clock className="w-7 h-7 text-primary" />
                                    {isId ? 'Rundown & Agenda KTT' : 'Summit Rundown & Agenda'}
                                </h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {isId ? 'Tempat:' : 'Venue:'} <strong className="text-foreground dark:text-white">Jakarta, Indonesia</strong>
                                </p>
                            </div>

                            <div className="flex items-center gap-3 bg-secondary/30 p-1.5 rounded-2xl border border-border">
                                <button
                                    onClick={() => setActiveDay('day1')}
                                    className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeDay === 'day1'
                                        ? 'bg-primary text-primary-foreground shadow-md'
                                        : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                >
                                    {isId ? 'Rabu, 7 Okt 2026' : 'Wednesday, 7 Oct 2026'}
                                </button>
                                <button
                                    onClick={() => setActiveDay('day2')}
                                    className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeDay === 'day2'
                                        ? 'bg-primary text-primary-foreground shadow-md'
                                        : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                >
                                    {isId ? 'Kamis, 8 Okt 2026' : 'Thursday, 8 Oct 2026'}
                                </button>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            {(activeDay === 'day1' ? rundownDay1 : rundownDay2).map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 rounded-2xl bg-secondary/20 hover:bg-secondary/40 border border-border/50 transition-colors">
                                    <div className="flex items-center gap-3.5">
                                        <span className="w-8 h-8 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                            {idx + 1}
                                        </span>
                                        <span className="font-bold text-sm text-foreground dark:text-white">
                                            {isId ? (item.activityId || item.activity) : item.activity}
                                        </span>
                                    </div>
                                    <span className="text-xs font-mono font-semibold text-primary bg-primary/10 px-3 py-1 rounded-lg">
                                        {item.time}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Important Dates & Submission Timeline */}
                    <div className="grid lg:grid-cols-12 gap-8 items-stretch mb-16">
                        <div className="lg:col-span-7 bg-white dark:bg-gray-900 rounded-3xl p-8 border border-border shadow-xl">
                            <h3 className="text-2xl font-bold text-foreground dark:text-white flex items-center gap-3 mb-6">
                                <Calendar className="w-6 h-6 text-primary" />
                                {isId ? 'Tanggal Penting & Lini Masa' : 'Important Dates & Timeline'}
                            </h3>
                            <div className="space-y-6 relative before:absolute before:inset-0 before:left-3.5 before:w-0.5 before:bg-border">
                                {importantDates.map((item, idx) => (
                                    <div key={idx} className="relative flex items-start gap-4 pl-8">
                                        <div className={`absolute left-1.5 top-1.5 w-4.5 h-4.5 rounded-full border-2 ${item.highlight
                                            ? 'bg-primary border-primary ring-4 ring-primary/20'
                                            : 'bg-white dark:bg-gray-800 border-muted-foreground'
                                            }`}></div>
                                        <div className="flex-1">
                                            <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-md inline-block mb-1 ${item.highlight
                                                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 font-extrabold border border-amber-500/30'
                                                : 'text-muted-foreground bg-secondary/20'
                                                }`}>
                                                {isId ? item.dateId : item.date}
                                            </span>
                                            <h4 className="font-bold text-base text-foreground dark:text-white">{isId ? (item.titleId || item.title) : item.title}</h4>
                                            <p className="text-xs text-muted-foreground mt-0.5">{isId ? (item.descId || item.desc) : item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Submission Link Card */}
                        <div className="lg:col-span-5 bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-950 dark:from-slate-900 dark:via-blue-950 dark:to-indigo-950 text-white rounded-3xl p-8 shadow-2xl border border-blue-700/40 dark:border-blue-500/30 flex flex-col justify-between relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 dark:bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/20 dark:bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

                            <div className="space-y-6 relative z-10">
                                <div>
                                    <span className="bg-amber-400 text-slate-950 font-extrabold text-xs px-3.5 py-1.5 rounded-full inline-block mb-4 shadow-md uppercase tracking-wider">
                                        {isId ? 'PENDAFTARAN DIBUKA SEKARANG' : 'SUBMISSION OPEN NOW'}
                                    </span>
                                    <h3 className="text-2xl sm:text-3xl font-extrabold leading-tight text-white">
                                        {isId ? 'Siap Mengirimkan Abstrak Diperluas Anda?' : 'Ready to Submit Your Extended Abstract?'}
                                    </h3>
                                    <p className="text-blue-100 dark:text-blue-200/90 text-sm mt-3 leading-relaxed">
                                        {isId ? <>Konferensi ini diselenggarakan <strong className="text-amber-300 font-bold">tanpa biaya (Rp 0)</strong>. Kirimkan abstrak diperluas (minimal 500 kata) sebelum <strong className="text-white underline font-bold">31 Juli 2026</strong>.</> : <>This conference is offered <strong className="text-amber-300 font-bold">free of charge (Rp 0)</strong>. Submit your extended abstract (minimum 500 words) before <strong className="text-white underline font-bold">July 31st, 2026</strong>.</>}
                                    </p>
                                </div>

                                {/* Official Links Box */}
                                <div className="p-4.5 rounded-2xl bg-white/10 dark:bg-slate-900/60 backdrop-blur-md border border-white/20 dark:border-white/15 space-y-3 text-xs sm:text-sm shadow-inner">
                                    <div className="flex items-center justify-between border-b border-white/15 pb-2.5">
                                        <span className="font-bold text-amber-300">{isId ? 'Folder Undangan Makalah:' : 'Call for Paper Drive:'}</span>
                                        <a href="https://drive.google.com/drive/folders/1rHoVAmo-apmYUhfjBdapgl32fmEb7HIR?usp=drive_link" target="_blank" rel="noopener noreferrer" className="text-white underline hover:text-amber-200 font-semibold transition-colors">{isId ? 'Buka Folder ↗' : 'Open Folder ↗'}</a>
                                    </div>
                                    <div className="flex items-center justify-between border-b border-white/15 pb-2.5">
                                        <span className="font-bold text-amber-300">{isId ? 'Templat Abstrak:' : 'Abstract Template:'}</span>
                                        <a href="https://docs.google.com/document/d/1_wNECU93sAw6XcAg3ZrumHQfHPAWKqQ7UKPJMMtHpAM/edit?usp=drive_link" target="_blank" rel="noopener noreferrer" className="text-white underline hover:text-amber-200 font-semibold transition-colors">{isId ? 'Unduh Dokumen ↗' : 'Download Doc ↗'}</a>
                                    </div>
                                    <div className="flex items-start gap-2.5 text-blue-100 dark:text-blue-200/90 pt-0.5">
                                        <MapPin className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                                        <span><strong className="text-white">{isId ? 'Tempat:' : 'Venue:'}</strong> Jakarta, Indonesia</span>
                                    </div>
                                    <div className="flex items-center gap-2.5 text-blue-100 dark:text-blue-200/90">
                                        <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                        <span><strong className="text-white">{isId ? 'Biaya:' : 'Fee:'}</strong> {isId ? 'Gratis Biaya Konferensi (Rp 0)' : 'Free Conference Fee (Rp 0)'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4 pt-6 relative z-10">
                                <a
                                    href="https://form.brin.go.id/s-f/joint-conference-submission-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full inline-flex items-center justify-center gap-2.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-extrabold py-4 px-6 rounded-xl shadow-lg hover:shadow-amber-400/30 transition-all duration-300 transform hover:scale-[1.02] text-base"
                                >
                                    <FileText className="w-5 h-5 flex-shrink-0" />
                                    <span>{isId ? 'Portal Formulir Pengajuan' : 'Submission Form Portal'}</span>
                                    <ExternalLink className="w-4 h-4 ml-1 flex-shrink-0 opacity-80" />
                                </a>

                                <div className="border-t border-white/20 pt-4 text-xs text-blue-100 dark:text-blue-200/80 space-y-1.5">
                                    <div className="font-bold text-white mb-1">{isId ? 'Kontak & Informasi:' : 'Contact & Inquiry:'}</div>
                                    <div>• Uswatun Hasanah, Ph.D. in Ed. (<a href="mailto:uswa002@brin.go.id" className="text-amber-300 dark:text-amber-400 underline hover:text-white transition-colors">uswa002@brin.go.id</a>)</div>
                                    <div>• Dr. Happy Fitria (<a href="mailto:happ002@brin.go.id" className="text-amber-300 dark:text-amber-400 underline hover:text-white transition-colors">happ002@brin.go.id</a>)</div>
                                    <div>• Research Center for Education-BRIN (+62 811-1064-6813)</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Back to Tracks Button */}
                    <div className="mt-16 text-center border-t border-border pt-10">
                        <Link
                            to="/#conference-tracks"
                            className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-white dark:bg-gray-800 hover:bg-primary hover:text-primary-foreground text-foreground dark:text-white font-bold text-base shadow-xl border border-border transition-all duration-300 transform hover:-translate-y-1 group"
                        >
                            <ArrowLeft className="w-5 h-5 text-amber-500 group-hover:text-primary-foreground transition-colors" />
                            {isId ? 'Kembali ke Daftar Track Konferensi' : 'Back to Conference Tracks'}
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BrinIseasJoinConference;